import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Card,
  Switch,
  InputNumber,
  Select,
  Space,
  Typography,
  Divider,
  Button,
  message,
  Checkbox,
  Row,
  Col,
  Spin,
  Flex,
} from 'antd';
import employeeStore from '../../store/EmployeeStore';
import axios from 'axios';
import { useBitrix } from '../../context/BitrixContext';

const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const SettingsPage = observer(() => {
  const { user, auth, loading: bitrixLoading } = useBitrix();

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [reminderDays, setReminderDays] = useState(30);
  const [ownerTask, setOwnerTask] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [localPermissions, setLocalPermissions] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const employeeOptions = employeeStore.employees.map((e) => ({
    label: e.name,
    value: e.id,
  }));
  console.log(user, auth)
  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      'X-Bitrix-User': user.ID,
      'X-Bitrix-Auth': auth,
    },
  });

  const permissionGroups = [
    {
      title: 'Удостоверения',
      items: [
        { key: 'canAddCertificates', label: 'Добавление' },
        { key: 'canViewCertificates', label: 'Просмотр' },
        { key: 'canEditCertificates', label: 'Редактирование' },
        { key: 'canDeleteCertificates', label: 'Удаление' },
      ],
    },
    {
      title: 'Объекты',
      items: [
        { key: 'canAddObjects', label: 'Добавление' },
        { key: 'canViewObjects', label: 'Просмотр' },
        { key: 'canEditObjects', label: 'Редактирование' },
        { key: 'canDeleteObjects', label: 'Удаление' },
      ],
    },
    {
      title: 'Оборудование',
      items: [
        { key: 'canAddEquipments', label: 'Добавление' },
        { key: 'canViewEquipments', label: 'Просмотр' },
        { key: 'canEditEquipments', label: 'Редактирование' },
        { key: 'canDeleteEquipments', label: 'Удаление' },
      ],
    },
    {
      title: 'Теги',
      items: [
        { key: 'canEditTags', label: 'Изменение' },
      ],
    },
    {
      title: 'Настройки',
      items: [
        { key: 'canChangeSettings', label: 'Изменение настроек' },
        { key: 'canChangePermissions', label: 'Изменение прав' },
      ],
    },
    {
      title: 'Администрирование',
      items: [
        { key: 'isAdmin', label: 'Администратор' },
      ],
    },
  ];

  // Load employees and global settings
  useEffect(() => {
    if (!user || !auth) return;

    const fetchData = async () => {
      try {
        setLoadingSettings(true);

        await employeeStore.fetchEmployees();

        const res = await axiosInstance.get('/settings');
        const data = res.data;

        setNotificationsEnabled(!!data.enableAutoTasks);
        setReminderDays(data.daysBeforeEvent || 1);
        setOwnerTask(data.ownerTask ?? true);

        const idsArray = (data.userIds || '')
          .split(',')
          .map(id => Number(id))
          .filter(Boolean);

        setSelectedEmployeeIds(idsArray);
        setSelectedEmployees(employeeStore.employees.filter(e => idsArray.includes(e.id)));
      } catch (e) {
        console.error(e);
        messageApi.error('Ошибка загрузки настроек');
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchData();
  }, [user, auth]);

  // Load permissions for selected user
  useEffect(() => {
    if (!selectedUser) {
      setLocalPermissions({});
      return;
    }

    const fetchPermissions = async () => {
      try {
        setLoadingPermissions(true);

        const res = await axiosInstance.get(`/permissions/${selectedUser}`);
        setLocalPermissions(res.data || {});
      } catch (e) {
        console.error(e);
        messageApi.error('Ошибка загрузки прав пользователя');
        setLocalPermissions({});

      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [selectedUser, user, auth]);

  const handlePermissionChange = (key, checked) => {
    setLocalPermissions(prev => ({ ...prev, [key]: checked }));
  };

const handleSave = async () => {
  const key = 'savingMessage';
  try {
    messageApi.loading({ content: 'Сохраняем настройки...', key });

    // Save global settings
    await axiosInstance.put('/settings', {
      enableAutoTasks: notificationsEnabled,
      daysBeforeEvent: reminderDays,
      userIds: selectedEmployeeIds.map(Number),
      ownerTask,
    });

    // Save permissions for selected user
    if (selectedUser) {
      await axiosInstance.post(`/permissions/${selectedUser}`, localPermissions);
    }

    // Show success message
    messageApi.success({ content: 'Настройки сохранены', key, duration: 2 });
  } catch (e) {
    console.error(e);
    messageApi.error({ content: 'Ошибка сохранения', key, duration: 2 });
  }
};


  if (loadingSettings || bitrixLoading) {
    return <Spin style={{ display: 'block', margin: '50px auto' }} />;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      {/* Notifications */}
            {contextHolder}

      <Card title="Уведомления" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Включить уведомления и автоматические задачи</span>
            <Switch checked={notificationsEnabled} onChange={setNotificationsEnabled} />
          </div>

          {notificationsEnabled && (
            <div style={{ paddingLeft: 24, marginTop: 16 }}>
              <div>
                За сколько дней до истечения напоминать:
                <InputNumber
                  min={1}
                  max={365}
                  value={reminderDays}
                  onChange={setReminderDays}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginTop: 8 }}>
                <Flex justify="space-between" align="center">
                  <span>Кому ставить задачу:</span>
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto', visibility: selectedEmployeeIds.length > 0 ? 'visible' : 'hidden' }} onClick={() => {
                    setSelectedEmployeeIds([]);
                    setSelectedEmployees([]);
                  }}>
                    Очистить
                  </Button>
                </Flex>
                <Select
                  mode="multiple"
                  placeholder="Выберите сотрудников"
                  value={selectedEmployeeIds}
                  onChange={(ids) => {
                    const nums = ids.map(Number);
                    setSelectedEmployeeIds(nums);
                    setSelectedEmployees(employeeStore.employees.filter(e => nums.includes(e.id)));
                  }}
                  style={{ marginTop: 4, width: '100%' }}
                  options={employeeOptions}
                  optionFilterProp="label"
                  maxTagCount="responsive"
                />
              </div>
            </div>
          )}
        </Space>
      </Card>

      {/* Access permissions */}
      <Card title="Права доступа">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            Выберите пользователя:
            <Select
              showSearch
              placeholder="Выберите сотрудника"
              value={selectedUser}
              onChange={setSelectedUser}
              style={{ marginTop: 4, width: '100%' }}
              options={employeeOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>

          {selectedUser && (
            <div style={{ marginTop: 16 }}>
              <Title level={5}>Права пользователя</Title>
              {loadingPermissions ? (
                <Spin />
              ) : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {permissionGroups.map((group) => (
                    <div key={group.title}>
                      <Text style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                        {group.title}
                      </Text>
                      <Row gutter={[8, 8]}>
                        {group.items.map((perm) => (
                          <Col key={perm.key} xs={24} sm={12} md={8}>
                            <Checkbox
                              checked={!!localPermissions[perm.key]}
                              onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                            >
                              {perm.label}
                            </Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ))}
                </Space>
              )}
            </div>
          )}
        </Space>
      </Card>

      <Divider />
      <Button type="primary" size="large" onClick={handleSave}>
        Сохранить
      </Button>
    </div>
  );
});

export default SettingsPage;
